import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Avatar
} from '@mui/material';
import { Email, Send, LockOutlined } from '@mui/icons-material';
import { adminAuthAPI } from '../../services/merchantAPI';

const AdminForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
  });

  const validateEmail = (value) => {
    if (!value || !value.trim()) return 'Email is required';
    // RFC 5322 compliant email regex that properly handles all valid email characters
    // This regex allows all valid special characters including +, -, ., etc.
    // It follows the official email specification more closely
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(value.trim())) return 'Please enter a valid email address';
    return '';
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError('');
    setSuccess('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      email: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Client-side validation - check for empty fields first
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors(prev => ({
        ...prev,
        email: emailError
      }));
      setLoading(false);
      return;
    }

    // Additional defensive check to ensure email is not empty (handles edge cases)
    if (!email.trim()) {
      setFieldErrors(prev => ({
        ...prev,
        email: 'Email is required'
      }));
      setLoading(false);
      return;
    }

    try {
      const response = await adminAuthAPI.forgotPassword(email);
      setSuccess(response.data.message || 'If your email exists, a password reset link has been sent.');
    } catch (err) {
      // Show all server errors as toast (general error)
      setError(err.response?.data?.error || 'Failed to send password reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            width: '100%'
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Enter your email address and we'll send you a password reset link.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Validation Error Alert */}
          {fieldErrors.email && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Please enter a valid email address.
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px', // Smaller font size on mobile
                    sm: '16px'  // Normal font size on larger screens
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                },
                '&:disabled': {
                  background: '#ccc',
                  color: '#666'
                }
              }}
              disabled={loading || !email.trim()}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <Send sx={{ mr: 1 }} /> Send Reset Link
                </>
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/glam-admin/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#667eea',
                  fontSize: '14px'
                }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminForgotPassword; 